import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IonicModule, LoadingController, ModalController, NavController, PopoverController } from '@ionic/angular';
import _ from 'lodash';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { IntegrationInputModalComponent } from 'src/app/components/integration-input-modal/integration-input-modal';
import { IntegrationOutputModalComponent } from 'src/app/components/integration-output-modal/integration-output-modal';
import { FEATURES, INTEGRATIONS, INTEGRATION_TYPES, INTEGRATION_TYPE_NAMES } from 'src/app/constants';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { NangoService } from 'src/services/nango.service';

import { AdminAppsRoutingModule } from './admin-apps-routing.module';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, AdminAppsRoutingModule, SharedPipesModule, NgxTippyModule],
  selector: 'app-admin-apps',
  templateUrl: './admin-apps.component.html',
  styleUrls: ['./admin-apps.component.scss'],
})
export class AdminAppsComponent implements OnInit {
  apps: any[] = [];
  isSavingIntegration = false;
  categories = ['All', ..._.values(INTEGRATION_TYPES)];
  categoryNames = INTEGRATION_TYPE_NAMES;
  category = this.categories[0];
  crmFeatureEnabled: boolean;
  taskFeatureEnabled: boolean;

  constructor(
    private route: ActivatedRoute,
    public authService: AuthService,
    private navCtrl: NavController,
    private msgSrvc: MessageService,
    private loadingCtrl: LoadingController,
    private apiService: ApiService,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private nangoService: NangoService,
  ) {}

  ngOnInit() {
    const queryParamTab = this.route.snapshot.queryParamMap.get('type');
    this.categories.forEach((cat) => {
      if (cat === queryParamTab) {
        this.category = cat;
      }
    });

    this.load();
  }

  load() {
    const tenant = this.authService.tenant;

    this.crmFeatureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.CRM_INTEGRATIONS);
    this.taskFeatureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.EXTERNAL_TASK_MANAGERS);

    this.apps = [];

    Object.keys(INTEGRATIONS).forEach((key) => {
      const app = INTEGRATIONS[key];
      const obj = {
        ...app,
        isGlobal: false,
        enabled: _.get(tenant, `integrations.${app.key}`) !== 'disabled',
      };

      if (this.category === 'All' || app.type === this.category) {
        this.apps.push(obj);
      }
    });
  }

  viewIntegration(app: any) {
    const root = `dashboard/settings`;
    let url = app.url;

    // URL to these is now dynamic depending on the project
    if (app.key === 'email' || app.key === 'widget' || app.key === 'microsoft-teams') {
      url = `projects/${this.authService.tenant.defaultProjectId}/${url}`;
    }
    // knowledge base related integrations have a different root
    if (app.type === INTEGRATION_TYPES.KNOWLEDGEBASE) {
      return this.navCtrl.navigateRoot(`dashboard/knowledge/articles`);
    }

    this.navCtrl.navigateRoot(`${root}/${url}`);
  }

  async reauthorizeApp(app: any) {
    const currentIntegrationSettings = this.authService.tenant.integrations[app.key] ?? {};
    this.isSavingIntegration = true;

    try {
      const nangoConfig = currentIntegrationSettings.nangoConfig;
      const nangoIntegrationId = currentIntegrationSettings.nangoIntegrationId ?? app.key;

      await this.nangoService.authorize(nangoIntegrationId, nangoConfig, undefined, app.authorizationParams);

      this.msgSrvc.show('Success!');
    } catch (err: any) {
      this.apiService.postPromise('/log-entries', {
        level: 'error',
        data: {
          message: err.message,
          stack: err.stack,
          code: err.code,
        },
      });

      this.msgSrvc.showError(err);
    } finally {
      this.isSavingIntegration = false;
    }
  }

  async requestAccess(app: any) {
    const currentIntegrationSettings = this.authService.tenant.integrations[app.key] ?? {};

    if (app.registerIntegration) {
      const modal = await this.modalCtrl.create({
        component: IntegrationInputModalComponent,
        componentProps: {
          integration: app,
          integrationSettings: currentIntegrationSettings,
        },
      });
      await modal.present();

      const { data } = await modal.onWillDismiss();
      if (data) {
        const output = await this.modalCtrl.create({
          component: IntegrationOutputModalComponent,
          componentProps: {
            integration: app,
            integrationOutput: data,
          },
        });
        await output.present();
      }
    } else if (currentIntegrationSettings?.mergeLink) {
      window.open(currentIntegrationSettings.mergeLink, '_blank');
    } else {
      window.open(`mailto:sales@unthread.io?subject=Beta+Access+to+${app.name}+Integration`, '_blank');
    }
  }

  async save() {
    const loader = await this.loadingCtrl.create({
      message: 'Updating dashboard...',
      spinner: 'crescent',
    });

    loader.present();

    // Only some services support global enablement via a service account
    // 'individual' means someone has to oauth their own personal account
    // Hopefully we expand this sometime soon
    const mapping = {
      [INTEGRATIONS.slack.key]: 'individual',
    };
    const integrations: any = {};

    this.apps.map((app) => {
      integrations[app.key] = app.enabled ? mapping[app.key] : 'disabled';
    });

    const updates = {
      integrations,
    };

    try {
      await this.authService.updateTenant(updates);

      this.load();

      loader.dismiss();
    } catch (err) {
      this.loadingCtrl.dismiss();
      this.msgSrvc.show(`Whoops! An error occurred: ${err.message}`);
    }
  }

  selectCategory(categoryType) {
    this.category = categoryType;
    this.navCtrl.navigateRoot([], {
      queryParams: {
        type: categoryType,
      },
      queryParamsHandling: 'merge',
    });
    this.load();
  }

  upgradeAccount() {
    this.navCtrl.navigateForward('/upgrade');
  }

  async disableIntegration(app: any) {
    const currentIntegrationSettings = this.authService.tenant.integrations[app.key] ?? {};
    this.isSavingIntegration = true;
    const nangoIntegrationId = currentIntegrationSettings.nangoIntegrationId ?? app.key;

    try {
      await this.apiService.postPromise(`/integrations/disable`, {
        integration: nangoIntegrationId,
        integrationType: app.type,
        isNango: app.isNango,
      });

      await this.authService.loadCompany();

      this.msgSrvc.show('Success!');
    } catch (err: any) {
      this.apiService.postPromise('/log-entries', {
        level: 'error',
        data: {
          message: err.message,
          stack: err.stack,
          code: err.code,
        },
      });

      this.msgSrvc.showError(err);
    } finally {
      this.isSavingIntegration = false;
    }
  }

  async openIntegrationOptions(app: any) {
    const DISABLE_INTEGRATION = 'Disable';
    const REAUTHORIZE_INTEGRATION = 'Reauthorize';
    const CHANGE_SETTINGS = 'Manage integration settings';

    const options: ActionDropdownType[] = [
      {
        label: CHANGE_SETTINGS,
      },
      {
        label: REAUTHORIZE_INTEGRATION,
      },
      {
        label: DISABLE_INTEGRATION,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (_index: number, label: string) => {
          if (label === DISABLE_INTEGRATION) {
            await this.disableIntegration(app);
          } else if (label === REAUTHORIZE_INTEGRATION) {
            await this.reauthorizeApp(app);
          } else if (label === CHANGE_SETTINGS) {
            this.requestAccess(app);
          }

          popover.dismiss();
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }
}
